import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import EntityMember from "_shared/entity-members/entity-member";
import EntityMemberSchema from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type TeamLeaderHandoverJSON = z.infer<typeof TeamLeaderHandoverSchema.TeamLeaderHandoverJSONZ>;
export type TeamLeaderHandoverParams = z.infer<typeof TeamLeaderHandoverSchema.TeamLeaderHandoverZ>;

/** The team a handover changes. The values are the names the API sends. */
export enum WorkTeamSide {
  Client = "Client",
  Vendor = "Vendor",
}

/**
 * What a leader handing over hired work wants done with a revision of it they opened and have not
 * seen through (#5836). The values are the names the API sends and takes.
 */
export enum InFlightRevisionDisposition {
  None = "None",
  HandOver = "HandOver",
  Cancel = "Cancel",
}

export default class TeamLeaderHandoverSchema {
  public static readonly TeamLeaderHandoverJSONZ = z.object({
    id: z.uuid(),
    side: z.enum(WorkTeamSide),
    outgoingLeader: EntityMemberSchema.EntityMemberJSONZ,
    successor: EntityMemberSchema.EntityMemberJSONZ,
    initiatedBy: EntityMemberSchema.EntityMemberJSONZ,
    approver: EntityMemberSchema.EntityMemberJSONZ.nullish(),
    outgoingLeaderStays: z.boolean(),
    grantSigningAuthority: z.boolean(),
    revisionDisposition: z.enum(InFlightRevisionDisposition),
    requestedDate: z.iso.datetime({ offset: true }),
    successorAcceptedDate: z.iso.datetime({ offset: true }).nullish(),
    approvedDate: z.iso.datetime({ offset: true }).nullish(),
  });

  public static readonly TeamLeaderHandoverZ = z.object({
    id: z.instanceof(Guid),
    side: z.enum(WorkTeamSide),
    outgoingLeader: z.instanceof(EntityMember),
    successor: z.instanceof(EntityMember),
    initiatedBy: z.instanceof(EntityMember),
    approver: z.instanceof(EntityMember).optional(),
    outgoingLeaderStays: z.boolean(),
    grantSigningAuthority: z.boolean(),
    revisionDisposition: z.enum(InFlightRevisionDisposition),
    requestedDate: DateSchema.type,
    successorAcceptedDate: DateSchema.type.optional(),
    approvedDate: DateSchema.type.optional(),
  });

  public static validate(values: unknown): TeamLeaderHandoverParams {
    return parseOrThrow(TeamLeaderHandoverSchema.TeamLeaderHandoverZ, values, {
      logError: true,
      schemaName: "TeamLeaderHandoverSchema",
      operation: "Validating TeamLeaderHandoverParams",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is TeamLeaderHandoverJSON {
    return TeamLeaderHandoverSchema.TeamLeaderHandoverJSONZ.safeParse(values).success;
  }

  public static deserialize(json: TeamLeaderHandoverJSON): TeamLeaderHandoverParams {
    return {
      id: Guid.fromJSON(json.id)!,
      side: json.side,
      outgoingLeader: EntityMember.fromJSON(json.outgoingLeader)!,
      successor: EntityMember.fromJSON(json.successor)!,
      initiatedBy: EntityMember.fromJSON(json.initiatedBy)!,
      approver: EntityMember.fromJSON(json.approver) ?? undefined,
      outgoingLeaderStays: json.outgoingLeaderStays,
      grantSigningAuthority: json.grantSigningAuthority,
      revisionDisposition: json.revisionDisposition,
      requestedDate: AHDate.fromJSON(json.requestedDate)!,
      successorAcceptedDate: AHDate.fromJSON(json.successorAcceptedDate) ?? undefined,
      approvedDate: AHDate.fromJSON(json.approvedDate) ?? undefined,
    };
  }

  public static serialize(handover: TeamLeaderHandoverParams): TeamLeaderHandoverJSON {
    return {
      id: handover.id.toJSON(),
      side: handover.side,
      outgoingLeader: handover.outgoingLeader.toJSON(),
      successor: handover.successor.toJSON(),
      initiatedBy: handover.initiatedBy.toJSON(),
      approver: handover.approver?.toJSON() ?? null,
      outgoingLeaderStays: handover.outgoingLeaderStays,
      grantSigningAuthority: handover.grantSigningAuthority,
      revisionDisposition: handover.revisionDisposition,
      requestedDate: handover.requestedDate.toJSON(),
      successorAcceptedDate: handover.successorAcceptedDate?.toJSON() ?? null,
      approvedDate: handover.approvedDate?.toJSON() ?? null,
    };
  }
}
